import React from 'react';
import { Package, Clock, CheckCircle, XCircle, Truck, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Order } from '../../types';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

interface PurchaseCardProps {
  order: Order;
}

export const PurchaseCard: React.FC<PurchaseCardProps> = ({ order }) => {
  const navigate = useNavigate();

  const getStatusIcon = () => {
    switch (order.status) {
      case 'delivered':
        return <CheckCircle className="text-green-500" size={20} />;
      case 'shipped':
        return <Truck className="text-blue-500" size={20} />;
      case 'rejected':
        return <XCircle className="text-red-500" size={20} />;
      case 'accepted':
        return <Check className="text-green-500" size={20} />;
      default:
        return <Clock className="text-yellow-500" size={20} />;
    }
  };

  const getStatusText = () => {
    switch (order.status) {
      case 'pending':
        return 'Order Placed';
      case 'accepted':
        return 'Accepted';
      case 'rejected':
        return 'Rejected';
      case 'shipped':
        return 'Shipped';
      case 'delivered':
        return 'Delivered';
      default:
        return 'Unknown';
    }
  };

  const getStatusColor = () => {
    switch (order.status) {
      case 'delivered':
        return 'bg-green-100 text-green-700';
      case 'shipped':
        return 'bg-blue-100 text-blue-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      case 'accepted':
        return 'bg-green-100 text-green-700';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formattedDate = format(order.createdAt, 'MMM dd, yyyy');
  const expectedDelivery = order.expectedDeliveryDate 
    ? format(order.expectedDeliveryDate, 'MMM dd, yyyy')
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100"
    >
      <div className="p-4">
        {/* Order Header */}
        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="text-xs text-gray-500">Order #{order.id.slice(0, 8)}</p>
            <p className="text-sm text-gray-600 mt-1">{formattedDate}</p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 ${getStatusColor()}`}>
            {getStatusIcon()}
            {getStatusText()}
          </div>
        </div>

        {/* Artwork Item */}
        <div 
          className="flex gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
          onClick={() => navigate(`/artwork/${order.artworkId}`)}
        >
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
            {order.artworkImage ? (
              <img 
                src={order.artworkImage} 
                alt={order.artworkTitle} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <Package size={24} />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 truncate">{order.artworkTitle}</h4>
            <p className="text-sm text-gray-500">by {order.sellerName}</p>
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-gray-500">Qty: {order.quantity}</span>
              <span className="font-bold text-primary">₹{order.price.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Order Summary */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-600">Total Amount</span>
            <span className="text-xl font-bold text-primary">₹{order.totalAmount.toLocaleString()}</span>
          </div>

          {/* Expected Delivery */}
          {expectedDelivery && order.status !== 'delivered' && order.status !== 'rejected' && (
            <div className="mb-3">
              <p className="text-xs text-gray-500">Expected Delivery</p>
              <p className="text-sm font-medium text-gray-700">{expectedDelivery}</p>
            </div>
          )}
          
          {/* Shipping Address */}
          {order.shippingAddress && (
            <div className="mt-3 p-3 bg-gray-50 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 mb-1">Shipping Address</p>
              <p className="text-xs text-gray-900 font-medium">{order.shippingAddress.name}</p>
              <p className="text-xs text-gray-600">{order.shippingAddress.phone}</p>
              <p className="text-xs text-gray-600 mt-1">
                {order.shippingAddress.street}
                {order.shippingAddress.landmark && `, ${order.shippingAddress.landmark}`}
              </p>
              <p className="text-xs text-gray-600">
                {order.shippingAddress.city}, {order.shippingAddress.state} - {order.shippingAddress.pincode}
              </p>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="mt-4 flex gap-2">
          <motion.button
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(`/orders/${order.id}`)}
            className="flex-1 py-2 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors text-sm"
          >
            View Details
          </motion.button>
          {order.status === 'delivered' && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 border-2 border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors text-sm"
              onClick={() => navigate(`/artwork/${order.artworkId}`)}
            >
              Buy Again
            </motion.button>
          )}
          {order.status === 'pending' && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 border-2 border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors text-sm"
            >
              Cancel
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
};